class InventoryDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.handleClick.bind(this));

    const overlay = this.querySelector('#InventoryDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
  }

  connectedCallback() {
    const openInventoryDrawerButton = document.getElementById('openInventoryDrawer');
    if (openInventoryDrawerButton) {
      openInventoryDrawerButton.addEventListener('click', () => {
        this.open();
      });
    }

    const closeInventoryDrawerButton = this.querySelector('.drawer__close');
    if (closeInventoryDrawerButton) {
      closeInventoryDrawerButton.addEventListener('click', () => {
        this.close();
      });
    }

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && this.classList.contains('active')) {
        this.close();
      }
    });

    // Add event listeners for color and size changes
    this.addInventoryUpdateListeners();

    this.loadInventory();
  }

  handleClick(event) {
    // Close drawer when clicking on the overlay or the drawer background
    if (
      event.target.id === 'InventoryDrawer-Overlay' ||
      event.target.classList?.contains('details-drawer__overlay') ||
      event.target === this
    ) {
      this.close();
    }
  }

  addInventoryUpdateListeners() {
    // Listen for color changes (if color swatches are clicked)
    const colorSwitcher = this.querySelector('[data-colour-switcher]');
    if (colorSwitcher) {
      colorSwitcher.addEventListener('click', (event) => {
        // Check if a color swatch was clicked
        const swatch = event.target.closest('[is="colour-swatch"]');
        if (swatch) {
          event.preventDefault();

          // Update the selected swatch styling
          this.updateSelectedColorSwatch(swatch);

          // Update size availability for the new color
          this.updateSizeAvailability();

          // Refresh inventory display
          setTimeout(() => this.showInventoryForSelectedVariant(), 100);
        }
      });
    }

    // Listen for size changes
    const sizeSwitcher = this.querySelector('[data-size-switcher]');
    if (sizeSwitcher) {
      sizeSwitcher.addEventListener('click', (event) => {
        // Check if a size was clicked
        const sizeElement = event.target.closest('div');
        if (sizeElement && sizeElement.parentElement === sizeSwitcher) {
          // Update the selected size styling
          this.updateSelectedSize(sizeElement);

          // Refresh inventory display
          setTimeout(() => this.showInventoryForSelectedVariant(), 100);
        }
      });
    }
  }

  updateSelectedColorSwatch(selectedSwatch) {
    // Remove selected styling from all swatches
    const allSwatches = this.querySelectorAll('[data-colour-switcher] [is="colour-swatch"]');
    allSwatches.forEach((swatch) => {
      swatch.classList.remove('border-black');
      swatch.classList.add('border-gray-300');
    });

    // Add selected styling to the clicked swatch
    selectedSwatch.classList.remove('border-gray-300');
    selectedSwatch.classList.add('border-black');

    // Update the color name display
    const colorNameElement = this.querySelector('[data-color-name]');
    if (colorNameElement) {
      const variantColorName = selectedSwatch.dataset.variantColourName;
      colorNameElement.textContent = variantColorName;
    }
  }

  updateSelectedSize(selectedSizeElement) {
    // Remove selected styling from all sizes
    const allSizes = this.querySelectorAll('[data-size-switcher] div');
    allSizes.forEach((size) => {
      size.classList.remove('bg-black', 'text-white', 'selected');
      size.classList.add('border-black');
    });

    // Add selected styling to the clicked size
    selectedSizeElement.classList.remove('border-black', 'opacity-50');
    selectedSizeElement.classList.add('bg-black', 'text-white', 'selected');
  }

  updateSizeAvailability() {
    const currentColor = this.querySelector('[data-color-name]')?.textContent;
    if (!currentColor || !this.inventoryData) return;

    const allSizes = this.querySelectorAll('[data-size-switcher] div');
    allSizes.forEach((sizeElement) => {
      const sizeName = sizeElement.textContent.trim();

      // Find this size/color combination in inventory data
      const variant = this.inventoryData.find((item) => item.colorName === currentColor && item.sizeName === sizeName);

      if (variant && variant.locations && variant.locations.length > 0) {
        // Check if any locations have stock
        const hasStock = variant.locations.some((loc) => parseInt(loc.soh) > 0);

        if (hasStock) {
          // Has stock - full opacity
          sizeElement.classList.remove('opacity-50');
        } else {
          // No stock - half opacity
          sizeElement.classList.add('opacity-50');
        }
      } else {
        // No inventory data - half opacity
        sizeElement.classList.add('opacity-50');
      }
    });
  }

  selectFirstAvailableSize() {
    const colorName = this.querySelector('[data-color-name]')?.textContent;
    if (!colorName || !this.inventoryData) return;

    const sizeElements = this.querySelectorAll('[data-size-switcher] div');
    for (const sizeElement of sizeElements) {
      const sizeName = sizeElement.textContent.trim();

      const variant = this.inventoryData.find((item) => item.colorName === colorName && item.sizeName === sizeName);

      if (variant && variant.locations && variant.locations.some((loc) => parseInt(loc.soh) > 0)) {
        this.updateSelectedSize(sizeElement);
        break;
      }
    }
  }

  loadInventory() {
    const apiUrl = this.dataset.apiUrl;
    const container = this.querySelector('#inventory-results');
    if (!apiUrl || !container) return;

    fetch(apiUrl)
      .then((res) => res.json())
      .then((data) => {
        this.inventoryData = data;

        // Select the first color swatch as default
        const firstSwatch = this.querySelector('[data-colour-switcher] [is="colour-swatch"]');
        if (firstSwatch) {
          this.updateSelectedColorSwatch(firstSwatch);
        }

        // Update size availability based on the selected color
        this.updateSizeAvailability();

        // Automatically select the first available size
        this.selectFirstAvailableSize();

        // Show inventory for the selected variant
        this.showInventoryForSelectedVariant();
      })
      .catch((err) => console.error('Failed to load store inventory', err));
  }

  showInventoryForSelectedVariant() {
    const container = this.querySelector('#inventory-results');
    const colorName = this.querySelector('[data-color-name]')?.textContent;
    const selectedSize = this.getSelectedSize();

    if (!colorName || !selectedSize) {
      container.innerHTML = '<p class="text-sm text-gray-600">Select a color and size to see store availability</p>';
      return;
    }

    // Find the variant data for the selected color and size
    const variant = this.inventoryData.find((item) => item.colorName === colorName && item.sizeName === selectedSize);

    if (!variant) {
      container.innerHTML = '<p class="text-sm text-gray-600">No inventory data available for this variant</p>';
      return;
    }

    this.displayVariantInventory(container, variant);
  }

  getSelectedSize() {
    // Get the currently selected size from the size switcher
    const selectedSizeElement = this.querySelector('[data-size-switcher] .selected');
    if (selectedSizeElement) {
      return selectedSizeElement.textContent.trim();
    }

    // No size selected - return null
    return null;
  }

  setInitialState() {
    const selectedSize = document.querySelector('.product-sizes .custom-dropdown-item.selected')?.innerText.trim();
    const sizeSwitcher = this.querySelector('[data-size-switcher]');
    if (selectedSize) {
      // Find the size element and initialise component widdit
      const sizeElements = sizeSwitcher.querySelectorAll('div');
      const sizeElement = Array.from(sizeElements).find((element) => element.textContent.trim() === selectedSize);
      if (sizeElement) {
        this.updateSelectedSize(sizeElement);
        // Colour is chosen by default and we have a size so update the inventory display
        this.showInventoryForSelectedVariant();
      }
    } else {
      // Fallback: automatically select the first available size
      this.selectFirstAvailableSize();
      this.showInventoryForSelectedVariant();
    }
  }

  displayVariantInventory(container, variant) {
    container.innerHTML = '';

    if (!variant.locations || variant.locations.length === 0) {
      container.innerHTML = '<p class="text-sm text-gray-600">This variant is not available in any stores</p>';
      return;
    }

    const locationsWithStock = variant.locations.filter((loc) => parseInt(loc.soh) > 0);

    if (locationsWithStock.length === 0) {
      container.innerHTML = '<p class="text-sm text-gray-600">This variant is out of stock in all stores</p>';
      return;
    }

    // Hydrate store data map (metaobjects first, then fallback)
    const metaJsonEl = document.getElementById('store-metaobjects-data');
    const fallbackJsonEl = document.getElementById('fallback-store-data');
    const storeByName = {};
    const stateByName = {};
    try {
      if (fallbackJsonEl?.textContent) {
        const fallbackData = JSON.parse(fallbackJsonEl.textContent);
        (fallbackData.stores || []).forEach((s) => {
          storeByName[s.name] = { ...s };
          stateByName[s.name] = s.state || 'Other';
        });
      }
    } catch (e) {
      console.error('Failed to parse fallback store data', e);
    }
    try {
      if (metaJsonEl?.textContent) {
        const metaData = JSON.parse(metaJsonEl.textContent);
        (metaData.stores || []).forEach((s) => {
          // Prefer metaobject values where present
          const existing = storeByName[s.name] || {};
          storeByName[s.name] = { ...existing, ...s };
          stateByName[s.name] = s.state || stateByName[s.name] || 'Other';
        });
      }
    } catch (e) {
      console.error('Failed to parse metaobject store data', e);
    }

    // Group locations by state
    const byState = {};
    locationsWithStock.forEach((loc) => {
      const info = storeByName[loc.location] || { name: loc.location };
      const state = stateByName[loc.location] || info.state || 'Other';
      if (!byState[state]) byState[state] = [];
      byState[state].push({ location: loc, info });
    });

    const template = this.querySelector('#store-inventory-template');

    Object.keys(byState)
      .sort((a, b) => a.localeCompare(b))
      .forEach((state) => {
        const groupHeader = document.createElement('h4');
        groupHeader.className = 'text-[11px] tracking-wide uppercase text-gray-500 px-2 pt-2';
        groupHeader.textContent = state;
        container.appendChild(groupHeader);

        const list = document.createElement('div');
        list.className = 'space-y-2';

        byState[state]
          .sort((a, b) => a.info.name.localeCompare(b.info.name))
          .forEach(({ location, info }) => {
            const node = template.content.cloneNode(true);
            const nameEl = node.querySelector('.store-item__name');
            const addrEl = node.querySelector('.store-item__address');
            const stockEl = node.querySelector('.store-item__stock');
            const detailsEl = node.querySelector('.store-item__details');
            const summaryBtn = node.querySelector('.store-item__summary');
            const hoursEl = node.querySelector('.store-item__hours');
            const phoneEl = node.querySelector('.store-item__phone');
            const callEl = node.querySelector('.store-item__call');
            const dirEl = node.querySelector('.store-item__directions');

            nameEl.textContent = info.name || location.location;
            addrEl.textContent = info.address || '';

            const stock = parseInt(location.soh);
            if (Number.isFinite(stock)) {
              stockEl.textContent = stock <= 3 ? `Low stock` : `In stock`;
              stockEl.classList.add(stock <= 3 ? 'text-amber-500' : 'text-green-500');
            }

            hoursEl.textContent = info.hours || '';
            phoneEl.textContent = info.phone || '';

            if (callEl && info.phone) {
              const tel = info.phone.replace(/[^+\d]/g, '');
              callEl.href = `tel:${tel}`;
            } else if (callEl) {
              callEl.remove();
            }

            if (dirEl) {
              let href = '';
              if (info.cord_lat && info.cord_long) {
                href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  `${info.cord_lat},${info.cord_long}`,
                )}`;
              } else if (info.address) {
                href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(info.address)}`;
              }
              if (href) dirEl.href = href;
              else dirEl.remove();
            }

            // Toggle logic: expand one at a time
            summaryBtn.addEventListener('click', () => {
              // Close other open details
              this.querySelectorAll('.store-item__details').forEach((el) => {
                if (el !== detailsEl) el.classList.add('hidden');
              });
              detailsEl.classList.toggle('hidden');
            });

            list.appendChild(node);
          });

        container.appendChild(list);
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // If user has selected a size on the product page, initialise with that
    this.setInitialState();

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('InventoryDrawer');
        const focusElement = this.querySelector('.drawer_inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateInventoryForVariant(variantId) {
    // Find the variant to get its data
    const variant = this.querySelector(`[data-variant-id="${variantId}"]`);
    if (!variant) return;

    // Get the variant handle for the API call
    const variantHandle = variant.dataset.variantHandle;

    // Update the API URL with the new variant handle
    const baseApiUrl = this.dataset.apiUrl;
    if (baseApiUrl && variantHandle) {
      // Replace the product handle in the URL with the variant handle
      const newApiUrl = baseApiUrl.replace(new RegExp(`productHandle=[^&]*`), `productHandle=${variantHandle}`);

      // Update the data attribute
      this.dataset.apiUrl = newApiUrl;

      // Reload inventory with the new variant
      this.loadInventory();
    }
  }
}

customElements.define('inventory-drawer', InventoryDrawer);
